import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const CallContext = createContext();

export const useCall = () => {
    const context = useContext(CallContext);
    if (!context) {
        throw new Error('useCall must be used within CallProvider');
    }
    return context;
};

export const CallProvider = ({ children }) => {
    const { userId } = useAuth();
    
    const [callState, setCallState] = useState({
        isInCall: false,
        isMinimized: false,
        callType: null,
        isMuted: false,
        otherUser: null,
        roomId: null
    });

    const socketRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteStreamRef = useRef(null);

    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);

    const iceServers = {
        iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' }
        ]
    };

    // Socket initialization
    useEffect(() => {
        // console.log('🔧 CallProvider mounted');
        // console.log('👤 Current userId:', userId);
        
        if (!userId) {
            // console.warn('⚠️  No userId provided to CallProvider');
            return;
        }

        const getSocketURL = () => {
            if (process.env.NODE_ENV === 'production') {
                return process.env.REACT_APP_API_URL || 'https://server-production-e45c.up.railway.app/';
            }
            return process.env.REACT_APP_API_URL || 'http://localhost:5000';
        };
        // console.log('🌐 Connecting to:', getSocketURL());

        const socket = io(getSocketURL(), {
            transports: ['websocket', 'polling']
        });

        socket.on('connect', () => {
            // console.log('✅ Socket connected:', socket.id);
            socket.emit('register', { userId });
            // console.log('📤 Sent register event with userId:', userId);
        });

        socket.on('connect_error', (error) => {
            // console.error('❌ Socket connection error:', error);
        });

        // ===== INCOMING CALL =====
        socket.on('call:incoming', ({ callerId, roomId, callerSocketId, callerInfo }) => {
            // console.log('\n📞 ===== INCOMING CALL =====');
            // console.log('   Caller ID:', callerId);
            // console.log('   Caller Info:', callerInfo);
            // console.log('   Room ID:', roomId);
            // console.log('   Caller Socket ID:', callerSocketId);
            
            // Check if already in a call
            setCallState(prev => {
                if (prev.isInCall) {
                    // console.log('   ⚠️  Already in a call, sending busy signal');
                    socket.emit('call:busy', { callerId });
                    return prev; // Don't update state
                }
                
                const newState = {
                    ...prev,
                    isInCall: true,
                    callType: 'incoming',
                    otherUser: { 
                        id: callerId, 
                        socketId: callerSocketId,
                        name: callerInfo?.name,
                        profilePicture: callerInfo?.profilePicture
                    },
                    roomId
                };
                // console.log('   Updated state:', newState);
                return newState;
            });
            // console.log('============================\n');
        });

        // ===== CALL BUSY =====
        socket.on('call:busy', () => {
            // console.log('\n📵 ===== CALL BUSY =====');
            endCall();
            alert('User is busy on another call');
            // console.log('========================\n');
        });

        // ===== CALL ACCEPTED =====
        socket.on('call:accepted', async ({ receiverId, receiverSocketId }) => {
            // console.log('\n✅ ===== CALL ACCEPTED =====');
            // console.log('   Receiver ID:', receiverId);
            // console.log('   Receiver Socket ID:', receiverSocketId);
            
            setCallState(prev => {
                const newState = {
                    ...prev,
                    callType: 'active',
                    otherUser: { ...prev.otherUser, id: receiverId, socketId: receiverSocketId }
                };
                // console.log('   Updated state:', newState);
                return newState;
            });
            
            // console.log('   Creating offer...');
            await createOffer(receiverSocketId);
            // console.log('============================\n');
        });

        // ===== CALL REJECTED =====
        socket.on('call:rejected', () => {
            // console.log('\n❌ ===== CALL REJECTED =====');
            endCall();
            alert('Call was rejected');
            // console.log('============================\n');
        });

        // ===== CALL ENDED =====
        socket.on('call:ended', () => {
            // console.log('\n🔴 ===== CALL ENDED =====');
            endCall();
            // console.log('============================\n');
        });

        // ===== WEBRTC OFFER =====
        socket.on('webrtc:offer', async ({ offer, from }) => {
            // console.log('\n🔄 ===== RECEIVED WEBRTC OFFER =====');
            // console.log('   From Socket:', from);
            // console.log('   Offer type:', offer.type);
            await handleOffer(offer, from);
            // console.log('====================================\n');
        });

        // ===== WEBRTC ANSWER =====
        socket.on('webrtc:answer', async ({ answer, from }) => {
            // console.log('\n🔄 ===== RECEIVED WEBRTC ANSWER =====');
            // console.log('   From Socket:', from);
            // console.log('   Answer type:', answer.type);
            await handleAnswer(answer);
            // console.log('=====================================\n');
        });

        // ===== ICE CANDIDATE =====
        socket.on('webrtc:ice-candidate', async ({ candidate, from }) => {
            // console.log('\n🧊 ===== RECEIVED ICE CANDIDATE =====');
            // console.log('   From:', from);
            await handleIceCandidate(candidate);
            // console.log('=====================================\n');
        });

        socket.on('disconnect', () => {
            // console.log('❌ Socket disconnected');
        });

        socketRef.current = socket;

        return () => {
            // console.log('🔌 Disconnecting socket');
            socket.disconnect();
        };
    }, [userId]);

    // const initializePeerConnection = () => {
    //     // console.log('🔗 Initializing peer connection...');
        
    //     if (peerConnectionRef.current) {
    //         // console.log('   ℹ️  Peer connection already exists');
    //         return peerConnectionRef.current;
    //     }

    //     const pc = new RTCPeerConnection(iceServers);
    //     // console.log('   ✅ Peer connection created');

    //     // Add local stream tracks
    //     if (localStreamRef.current) {
    //         // console.log('   🎤 Adding local tracks to peer connection');
    //         localStreamRef.current.getTracks().forEach((track, index) => {
    //             pc.addTrack(track, localStreamRef.current);
    //             // console.log(`      Track ${index}: ${track.kind}, enabled: ${track.enabled}`);
    //         });
    //     }

    //     // Handle incoming remote tracks
    //     pc.ontrack = (event) => {
    //         // console.log('🔊 ===== REMOTE TRACK RECEIVED =====');
    //         // console.log('   Stream:', event.streams[0]);
    //         // console.log('   Track kind:', event.track.kind);
    //         // console.log('   Track enabled:', event.track.enabled);
    //         // console.log('   Track muted:', event.track.muted);
    //         // console.log('   Track readyState:', event.track.readyState);
            
    //         // CRITICAL FIX: Enable the track if it's disabled
    //         if (!event.track.enabled) {
    //             event.track.enabled = true;
    //             // console.log('   ✅ Track enabled');
    //         }
            
    //         // Listen for unmute event
    //         event.track.onunmute = () => {
    //             // console.log('   🔊 Track unmuted!');
    //         };
            
    //         event.track.onmute = () => {
    //             // console.log('   🔇 Track muted!');
    //         };
            
    //         remoteStreamRef.current = event.streams[0];
            
    //         // Force re-render to update audio element
    //         setCallState(prev => ({ ...prev, _updateTrigger: Date.now() }));
    //         // console.log('====================================\n');
    //     };

    //     // Handle ICE candidates
    //     pc.onicecandidate = (event) => {
    //         if (event.candidate && socketRef.current && callState.otherUser?.socketId) {
    //             // console.log('🧊 Sending ICE candidate to:', callState.otherUser.socketId);
    //             socketRef.current.emit('webrtc:ice-candidate', {
    //                 candidate: event.candidate,
    //                 to: callState.otherUser.socketId
    //             });
    //         } else if (!event.candidate) {
    //             // console.log('🧊 All ICE candidates sent');
    //         }
    //     };

    //     pc.oniceconnectionstatechange = () => {
    //         console.log('🔗 ICE Connection State:', pc.iceConnectionState);
    //         if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
    //             console.error('❌ ICE connection failed/disconnected');
    //         }
    //     };

    //     pc.onconnectionstatechange = () => {
    //         // console.log('🔗 Connection State:', pc.connectionState);
    //         if (pc.connectionState === 'connected') {
    //             // console.log('✅ Peer connection established successfully!');
    //         }
    //     };

    //     peerConnectionRef.current = pc;
    //     return pc;
    // };

    const otherSocketIdRef = useRef(null);
const iceQueueRef = useRef([]);

    const initializePeerConnection = () => {
        if (peerConnectionRef.current) return peerConnectionRef.current;

        const pc = new RTCPeerConnection(iceServers);

        // 🔴 LOCAL AUDIO TRACK ADD (MOST IMPORTANT)
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => {
                pc.addTrack(track, localStreamRef.current);
            });
        }

        // 🔴 REMOTE AUDIO FIX
        pc.ontrack = (event) => {
            if (!remoteStreamRef.current) {
                remoteStreamRef.current = new MediaStream();
            }

            remoteStreamRef.current.addTrack(event.track);
            event.track.enabled = true;

            setCallState(prev => ({ ...prev, _force: Date.now() }));
        };

        pc.onicecandidate = (event) => {
            if (event.candidate && socketRef.current && otherSocketIdRef.current) {
                socketRef.current.emit('webrtc:ice-candidate', {
                    candidate: event.candidate,
                    to: otherSocketIdRef.current
                });
            }
        };

        peerConnectionRef.current = pc;
        return pc;
    };

    // const createOffer = async (toSocketId) => {
    //     // console.log('📤 Creating WebRTC offer for socket:', toSocketId);
    //     try {
    //         const pc = initializePeerConnection();
            
    //         const offer = await pc.createOffer({
    //             offerToReceiveAudio: true
    //         });
            
    //         await pc.setLocalDescription(offer);
    //         // console.log('   ✅ Local description set');

    //         socketRef.current.emit('webrtc:offer', {
    //             offer,
    //             to: toSocketId
    //         });
    //         // console.log('   📤 Offer sent');
    //     } catch (error) {
    //         // console.error('   ❌ Error creating offer:', error);
    //     }
    // };

    const createOffer = async (toSocketId) => {
    if (!localStreamRef.current) {
        await getLocalStream();
    }

    otherSocketIdRef.current = toSocketId;

    const pc = initializePeerConnection();

    const offer = await pc.createOffer({
        offerToReceiveAudio: true
    });

    await pc.setLocalDescription(offer);

    socketRef.current.emit('webrtc:offer', {
        offer,
        to: toSocketId
    });
};

    // const handleOffer = async (offer, from) => {
    //     // console.log('📥 Handling WebRTC offer from:', from);
    //     try {
    //         await getLocalStream();
    //         const pc = initializePeerConnection();
            
    //         await pc.setRemoteDescription(new RTCSessionDescription(offer));
    //         // console.log('   ✅ Remote description set');

    //         const answer = await pc.createAnswer();
    //         await pc.setLocalDescription(answer);
    //         // console.log('   ✅ Local description (answer) set');

    //         socketRef.current.emit('webrtc:answer', {
    //             answer,
    //             to: from
    //         });
    //         // console.log('   📤 Answer sent to:', from);
    //     } catch (error) {
    //         // console.error('   ❌ Error handling offer:', error);
    //     }
    // };

    const handleOffer = async (offer, from) => {
        if (!localStreamRef.current) {
            await getLocalStream();
        }

        otherSocketIdRef.current = from;

        const pc = initializePeerConnection();

        await pc.setRemoteDescription(new RTCSessionDescription(offer));

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socketRef.current.emit('webrtc:answer', {
            answer,
            to: from
        });
    };

    // const handleAnswer = async (answer) => {
    //     // console.log('📥 Handling WebRTC answer');
    //     try {
    //         if (!peerConnectionRef.current) {
    //             console.error('   ❌ No peer connection exists!');
    //             return;
    //         }
            
    //         await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    //         // console.log('   ✅ Remote description set');
    //     } catch (error) {
    //         // console.error('   ❌ Error handling answer:', error);
    //     }
    // };

    const handleAnswer = async (answer) => {
        const pc = peerConnectionRef.current;
        if (!pc) return;

        await pc.setRemoteDescription(new RTCSessionDescription(answer));

        iceQueueRef.current.forEach(c =>
            pc.addIceCandidate(new RTCIceCandidate(c))
        );
        iceQueueRef.current = [];
    };

    const handleIceCandidate = async (candidate) => {
        // console.log('📥 Handling ICE candidate');
        try {
            if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
                await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
                // console.log('   ✅ ICE candidate added');
            } else {
                // console.warn('   ⚠️  Peer connection not ready, queuing candidate');
            }
        } catch (error) {
            // console.error('   ❌ Error handling ICE candidate:', error);
        }
    };

    const getLocalStream = async () => {
        // console.log('🎤 Requesting microphone access...');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            localStreamRef.current = stream;
            // console.log('   ✅ Microphone access granted');
            // console.log('   Tracks:', stream.getTracks().map(t => `${t.kind}: ${t.enabled}`));
            return stream;
        } catch (error) {
            // console.error('   ❌ Error accessing microphone:', error);
            alert('Could not access microphone. Please allow microphone access.');
            throw error;
        }
    };

    const initiateCall = async (receiverId, roomId, receiverInfo = {}) => {
        // console.log('\n📞 ===== INITIATING CALL =====');
        // console.log('   Caller (me):', userId);
        // console.log('   Receiver:', receiverId);
        // console.log('   Room:', roomId);
        
        try {
            await getLocalStream();
            
            const newState = {
                isInCall: true,
                isMinimized: false,
                callType: 'outgoing',
                otherUser: { 
                    id: receiverId,
                    name: receiverInfo.name,
                    profilePicture: receiverInfo.profilePicture
                },
                roomId
            };
            
            setCallState(newState);
            // console.log('   Updated state:', newState);

            socketRef.current.emit('call:initiate', {
                callerId: userId,
                receiverId,
                roomId,
                callerInfo: {
                    name: receiverInfo.callerName || 'Unknown User',
                    profilePicture: receiverInfo.callerProfilePicture || null
                }
            });
            // console.log('   📤 Sent call:initiate event');
            // console.log('==============================\n');
        } catch (error) {
            // console.error('   ❌ Error initiating call:', error);
        }
    };

    const acceptCall = async () => {
        // console.log('\n✅ ===== ACCEPTING CALL =====');
        try {
            await getLocalStream();
            
            setCallState(prev => {
                const newState = {
                    ...prev,
                    callType: 'active'
                };
                // console.log('   Updated state:', newState);
                return newState;
            });

            socketRef.current.emit('call:accept', {
                callerId: callState.otherUser.id,
                receiverId: userId
            });
            // console.log('   📤 Sent call:accept event');
            // console.log('============================\n');
        } catch (error) {
            // console.error('   ❌ Error accepting call:', error);
        }
    };

    const rejectCall = () => {
        // console.log('\n❌ ===== REJECTING CALL =====');
        // console.log('   Rejecting call from:', callState.otherUser.id);
        
        socketRef.current.emit('call:reject', {
            callerId: callState.otherUser.id
        });
        // console.log('   📤 Sent call:reject event');
        endCall();
        // console.log('============================\n');
    };

    const endCall = () => {
        // console.log('\n🔴 ===== ENDING CALL =====');
        
        if (callState.otherUser && socketRef.current) {
            // console.log('   Notifying:', callState.otherUser.id);
            socketRef.current.emit('call:end', {
                userId: callState.otherUser.id
            });
        }

        if (peerConnectionRef.current) {
            // console.log('   Closing peer connection');
            peerConnectionRef.current.close();
            peerConnectionRef.current = null;
        }

        if (localStreamRef.current) {
            // console.log('   Stopping local stream');
            localStreamRef.current.getTracks().forEach(track => {
                track.stop();
                // console.log('      Stopped track:', track.kind);
            });
            localStreamRef.current = null;
        }

        remoteStreamRef.current = null;

        setCallState({
            isInCall: false,
            isMinimized: false,
            callType: null,
            otherUser: null,
            roomId: null
        });
        // console.log('   Call state reset');
        // console.log('==========================\n');
    };

    
    const toggleMute = () => {
        const stream = localStreamRef.current;
        if (!stream) return;

        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) return;

        audioTrack.enabled = !audioTrack.enabled;

        setCallState(prev => ({
            ...prev,
            isMuted: !audioTrack.enabled ? true : false
        }));
    };


    const toggleMinimize = () => {
        // console.log('🔄 Toggling minimize');
        setCallState(prev => ({
            ...prev,
            isMinimized: !prev.isMinimized
        }));
    };

    const value = {
        callState,
        initiateCall,
        acceptCall,
        rejectCall,
        endCall,
        toggleMinimize,
        localStream: localStreamRef.current,
        remoteStream: remoteStreamRef.current,
        toggleMute
    };

    return (
        <CallContext.Provider value={value}>
            {children}
        </CallContext.Provider>
    );
};